import { PayloadAction, createSlice } from "@reduxjs/toolkit"

import { RootState } from "../store"
import { subscriptionsApi } from "../queries/subscriptions"
import { leadsApi } from "../queries/lead"
import { TabRangeState, EngagementsTab, TabDateFilter } from "../../types"
import { DateRangePreset } from "../../common/ui/activity/date-filter"

export const engagementsSliceName = "engagements"

export interface EngagementsState {
  tabs: {
    range: TabRangeState
  }
  counts: {
    engagements: number | null
    leads: number | null
  }
}

const initialState: EngagementsState = {
  tabs: {
    range: Object.values(EngagementsTab).reduce((acc, key) => {
      acc[key] = {
        range: key === EngagementsTab.activity ? "last_30" : "last_7",
        customStartDate: null,
        customEndDate: null
      }
      return acc
    }, {} as TabRangeState)
  },
  counts: {
    engagements: null,
    leads: null
  }
}

const engagementsSlice = createSlice({
  name: engagementsSliceName,
  initialState,
  reducers: {
    updateRange: (
      state,
      action: PayloadAction<{
        tabKey: EngagementsTab
        range: DateRangePreset
        customStartDate?: Date | null
        customEndDate?: Date | null
      }>
    ) => {
      const { tabKey, range, customStartDate, customEndDate } = action.payload
      state.tabs.range[tabKey] = {
        range,
        customStartDate: customStartDate || null,
        customEndDate: customEndDate || null
      }
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      subscriptionsApi.endpoints.fetchSubscriptionLogs.matchFulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.counts.engagements = action.payload?.length ?? 0
      }
    )
    builder.addMatcher(
      leadsApi.endpoints.fetchLeadLogs.matchFulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.counts.leads = action.payload?.length ?? 0
      }
    )
  }
})

const { reducer, actions } = engagementsSlice

export const { updateRange } = actions

export const selectTabRange = (state: RootState, tabKey: EngagementsTab) =>
  state.engagements.tabs.range[tabKey]

export const selectHasEngagements = (state: RootState) =>
  (state.engagements.counts.engagements ?? 0) > 0

export const selectHasLeads = (state: RootState) =>
  (state.engagements.counts.leads ?? 0) > 0

export const engagementsReducer = reducer
